import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import ExternalPrestadorRedirect from './components/auth/ExternalPrestadorRedirect'
import AppLayout from './components/navigation/AppLayout'
import InstallPrompt from './components/pwa/InstallPrompt'
import { AccessibilityProvider } from './components/ui/AccessibilityProvider'
import { usePWA } from './hooks/usePWA'
import { useServiceWorker, usePushNotifications } from './hooks/useServiceWorker'
import { useAuth } from './hooks/useAuth'
import { analyticsClient } from './lib/analytics-client'
import { paginaVista } from './lib/analytics'
import { AvisoCookies } from './components/legal/AvisoCookies'
import { AceitarPoliticas } from './components/legal/AceitarPoliticas'
import { cacheUtils } from './lib/cache'
import { AuthProvider } from './providers/AuthProvider'
import { UploadService } from './services/uploadService'

// Quick Wins - Providers
import { NotificationProvider, ThemeProvider } from './components/quick-wins'

// Sentry Error Boundary para captura de erros
import SentryErrorBoundary from './components/SentryErrorBoundary'

// Sistema de lazy loading inteligente
import {
  LazyComponentWrapper,
  smartLazyLoader,
  type LazyLoadConfig
} from './utils/lazyLoading.tsx'

// Páginas principais (carregamento imediato)
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'))
const Cookies = lazy(() => import('./pages/Cookies'))
const Imprensa = lazy(() => import('./pages/Imprensa'))
import Index from './pages/Index'
import { CanonicalPadrao } from './components/seo/CanonicalPadrao'
const Licencas = lazy(() => import('./pages/Licencas'))
const Privacidade = lazy(() => import('./pages/Privacidade'))
const MeusDados = lazy(() => import('./pages/MeusDados'))
const ServicosInfo = lazy(() => import('./pages/ServicosInfo'))
const Termos = lazy(() => import('./pages/Termos'))
const AuthCallback = lazy(() => import('./pages/auth/Callback'))
const Login = lazy(() => import('./pages/auth/Login'))
const Register = lazy(() => import('./pages/auth/Register'))
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))
const Onboarding = lazy(() => import('./pages/Onboarding'))
const NotFound = lazy(() => import('./pages/NotFound'))
const MeusTickets = lazy(() => import('./pages/MeusTickets'))
const ReportarProblema = lazy(() => import('./pages/ReportarProblema'))
const PromocaoDetalhe = lazy(() => import('./pages/PromocaoDetalhe'))
const TorneSePrestador = lazy(() => import('./pages/TorneSePrestador'))
const PedirServico = lazy(() => import('./pages/PedirServico'))
const PedidoEstado = lazy(() => import('./pages/PedidoEstado'))
const PartilharEstado = lazy(() => import('./pages/PartilharEstado'))
const ServicosLuanda = lazy(() => import('./pages/ServicosLuanda'))
const ProfissaoLuanda = lazy(() => import('./pages/ProfissaoLuanda'))

// Configurações de lazy loading para páginas
const LAZY_PAGES: Record<string, LazyLoadConfig> = {
  // Páginas de prestador (alta prioridade)
  PrestadorPublicProfile: {
    name: 'PrestadorPublicProfile',
    importFn: () => import('./pages/PrestadorPublicProfile'),
    priority: 'high',
    preload: true
  },
  // Páginas de serviços (alta prioridade)
  Servicos: {
    name: 'ServicosPage',
    importFn: () => import('./pages/Servicos'),
    priority: 'high',
    preload: true
  },
  ServicoDetalhe: {
    name: 'ServicoDetalheOptimized',
    importFn: () => import('./pages/ServicoDetalheOptimized'),
    priority: 'high',
    preload: true
  },
  
  // Páginas de categorias (média prioridade)
  Categorias: {
    name: 'CategoriasPage',
    importFn: () => import('./pages/Explore'),
    priority: 'medium',
    preload: true
  },
  Subcategorias: {
    name: 'SubcategoriasPage',
    importFn: () => import('./pages/Subcategorias'),
    priority: 'medium',
    preload: true
  },
  
  // Páginas de usuário (média prioridade)
  Dashboard: {
    name: 'DashboardPage',
    importFn: () => import('./pages/Dashboard'),
    priority: 'medium',
    preload: true
  },
  Profile: {
    name: 'ProfilePage',
    importFn: () => import('./pages/Profile'),
    priority: 'medium',
    preload: true
  },
  
  // Páginas de prestadores (baixa prioridade)
  Prestadores: {
    name: 'PrestadoresPage',
    importFn: () => import('./pages/Prestadores'),
    priority: 'low',
    preload: false
  },
  // Páginas de ajuda e suporte (baixa prioridade)
  Ajuda: {
    name: 'AjudaPage',
    importFn: () => import('./pages/Ajuda'),
    priority: 'low',
    preload: false
  },
  Sobre: {
    name: 'SobrePage',
    importFn: () => import('./pages/Sobre'),
    priority: 'low',
    preload: false
  },
  
  // Páginas de funcionalidades do usuário (média prioridade)
  // 10/10/2026: o fluxo de marcações foi substituído pelo pedido — /booking
  // redireciona para /pedir (com o link pessoal do prestador, se vier um).
  Booking: {
    name: 'RedirecionarParaPedido',
    importFn: () => import('./pages/RedirecionarParaPedido'),
    priority: 'medium',
    preload: false
  },
  // /nearby-search = "Na sua zona" (por zona, não GPS — ver NaMinhaZona.tsx).
  NearbySearch: {
    name: 'NaMinhaZonaPage',
    importFn: () => import('./pages/NaMinhaZona'),
    priority: 'medium',
    preload: false
  },
  Favorites: {
    name: 'FavoritesPage',
    importFn: () => import('./pages/Favorites'),
    priority: 'medium',
    preload: false
  },
  Appointments: {
    name: 'AppointmentsPage',
    importFn: () => import('./pages/Appointments'),
    priority: 'medium',
    preload: false
  },
  Avaliacoes: {
    name: 'AvaliacoesPage',
    importFn: () => import('./pages/AvaliacoesHistorico'),
    priority: 'medium',
    preload: false
  },
  AvaliarServico: {
    name: 'AvaliarServicoPage',
    importFn: () => import('./pages/AvaliarServico'),
    priority: 'medium',
    preload: false
  },
  Atividade: {
    name: 'AtividadePage',
    importFn: () => import('./pages/Atividade'),
    priority: 'high',
    preload: true
  },
  Messages: {
    name: 'MessagesPage',
    importFn: () => import('./pages/Messages'),
    priority: 'medium',
    preload: false
  },
  Promocoes: {
    name: 'PromocoesPage',
    importFn: () => import('./pages/Promocoes'),
    priority: 'medium',
    preload: false
  },
  Settings: {
    name: 'SettingsPage',
    importFn: () => import('./pages/Settings'),
    priority: 'medium',
    preload: false
  },
  
  // Páginas adicionais úteis (baixa prioridade)
  Contato: {
    name: 'ContatoPage',
    importFn: () => import('./pages/Contato'),
    priority: 'low',
    preload: false
  },
  Carreiras: {
    name: 'CarreirasPage',
    importFn: () => import('./pages/Carreiras'),
    priority: 'low',
    preload: false
  },
  // Quick Wins - Páginas (média prioridade)
  Notificacoes: {
    name: 'NotificacoesPage',
    importFn: () => import('./pages/Notificacoes'),
    priority: 'medium',
    preload: true
  }
}

// Componente de loading otimizado
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <div className="absolute inset-0 rounded-full border-2 border-gray-200"></div>
      </div>
      <p className="text-sm text-muted-foreground">Carregando...</p>
    </div>
  </div>
)

// Componente para prefetch inteligente baseado em navegação
function NavigationPrefetcher() {
  const location = useLocation()
  
  useEffect(() => {
    smartLazyLoader.prefetchOnNavigation(location.pathname)
  }, [location.pathname])
  
  return null
}

// Página vista no Google Analytics a cada mudança de rota (incluindo a primeira).
function RegistarPaginaVista() {
  const location = useLocation()

  useEffect(() => {
    paginaVista()
  }, [location.pathname, location.search])

  return null
}

// Inicialização silenciosa de Web Push Notifications
function PushNotificationInit() {
  const { user } = useAuth()
  usePushNotifications(user?.id ?? undefined)
  return null
}

// Componente de redirecionamento seguro para /prestador/:id -> /prestador/perfil/:id
function PrestadorRedirect() {
  const { id } = useParams()
  const location = useLocation()
  
  const targetPath = `/prestador/perfil/${id || ''}`
  
  if (location.pathname === targetPath) {
    return null
  }
  
  return <Navigate to={targetPath} replace />
}

function App() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: 1,
        retryDelay: 1000,
      },
    },
  }))
  const [swState, swActions] = useServiceWorker()
  const pwaState = usePWA()

  const initializeEssentialServices = () => {
    try {
      if (import.meta.env.DEV) console.log('🔧 Inicializando serviços essenciais...');
      
      if (typeof window !== 'undefined') {
        try {
          localStorage.getItem('test');
        } catch (error) {
          console.warn('⚠️ localStorage não disponível:', error);
        }
      }
      
      if (import.meta.env.DEV) console.log('✅ Serviços essenciais inicializados');
    } catch (error) {
      console.error('❌ Erro ao inicializar serviços essenciais:', error);
    }
  };

  useEffect(() => {
    initializeEssentialServices();
    UploadService.initializeServices();
    
    try {
      cacheUtils.startCleanup()
    } catch (error) {
      console.error('❌ Erro ao inicializar cache:', error)
    }
    
    try {
      analyticsClient.trackPageView({
        page_title: document.title
      })
    } catch (error) {
      console.error('❌ Erro ao configurar analytics:', error)
    }
    
    if (swState.isSupported && !swState.isInstalled) {
      swActions.register().catch(error => {
        console.error('❌ Erro ao registrar Service Worker:', error)
      })
    }
    
    if (import.meta.env.DEV) console.log('🚀 TaMoAki iniciado com sucesso!')
    
    const logCacheStats = () => {
      if (import.meta.env.DEV) {
        const stats = smartLazyLoader.getCacheStats()
        console.log('📊 Estatísticas de Lazy Loading:', stats)
      }
    }
    
    logCacheStats()
    
    if (import.meta.env.DEV) {
      const interval = setInterval(logCacheStats, 5 * 60 * 1000)
      return () => clearInterval(interval)
    }
  }, [swState.isSupported, swState.isInstalled, swActions, pwaState])

  return (
    <QueryClientProvider client={queryClient}>
      <NavigationPrefetcher />
      <RegistarPaginaVista />
      <CanonicalPadrao />
      {/* 23/10/2026: aviso de cookies em todas as páginas (antes só na inicial). */}
      <AvisoCookies />
      
      <ThemeProvider>
        <AccessibilityProvider>
          <AuthProvider>
            <NotificationProvider>
              <PushNotificationInit />
              {/* 26/10/2026: aceitação da versão em vigor dos Termos e da Privacidade. */}
              <AceitarPoliticas />
              <SentryErrorBoundary>
                <div className="min-h-screen bg-background">
                  {/* 07/10/2026: páginas fora da inicial carregam à parte (React.lazy). */}
                  <Suspense fallback={<PageLoading />}>
                  <Routes>
                    {/* OAuth callback */}
                    <Route path="/oauth/callback" element={<OAuthCallback />} />
                    
                    {/* Rotas de Autenticação e Landing (sem layout) */}
                    <Route path="/" element={<Index />} />
                    <Route path="/torne-se-prestador" element={<TorneSePrestador />} />
                    <Route path="/pedir" element={<PedirServico />} />
                    <Route path="/pedido/:codigo" element={<PedidoEstado />} />
                    <Route path="/partilhar/:codigo" element={<PartilharEstado />} />
                    {/* Páginas por profissão para o Google (HTML estático gerado no build). */}
                    <Route path="/luanda" element={<ServicosLuanda />} />
                    <Route path="/luanda/:slug" element={<ProfissaoLuanda />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                    <Route path="/auth/reset-password" element={<ResetPassword />} />
                    
                    {/* Páginas públicas legais */}
                    <Route path="/termos" element={<Termos />} />
                    <Route path="/privacidade" element={<Privacidade />} />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/meus-dados" element={<MeusDados />} />
                    <Route path="/licencas" element={<Licencas />} />
                    
                    {/* Páginas institucionais públicas */}
                    <Route path="/info-servicos" element={<ServicosInfo />} />
                    <Route
                      path="/sobre"
                      element={
                        <Suspense fallback={<PageLoading />}>
                          <LazyComponentWrapper config={LAZY_PAGES.Sobre} />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/carreiras"
                      element={
                        <Suspense fallback={<PageLoading />}>
                          <LazyComponentWrapper config={LAZY_PAGES.Carreiras} />
                        </Suspense>
                      }
                    />
                    <Route path="/imprensa" element={<Imprensa />} />
                    <Route
                      path="/contato"
                      element={
                        <Suspense fallback={<PageLoading />}>
                          <LazyComponentWrapper config={LAZY_PAGES.Contato} />
                        </Suspense>
                      }
                    />

                    {/* Rotas com layout da aplicação */}
                    <Route path="/" element={<AppLayout />}>
                      <Route
                        path="dashboard"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Dashboard} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="profile"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Profile} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      {/* Catálogo de Serviços do Utilizador */}
                      <Route
                        path="servicos"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Servicos} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="servicos/:id"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.ServicoDetalhe} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="categorias"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Categorias} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="explore"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Categorias} />
                          </Suspense>
                        }
                      />

                      {/* Rotas dinâmicas de Categorias / Subcategorias */}
                      <Route
                        path="categorias/:categoriaId/:subcategoriaId"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Servicos} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="categorias/:categoriaId/:subcategoriaId/servico/:servicoId"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.ServicoDetalhe} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="categorias/:id"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Subcategorias} />
                          </Suspense>
                        }
                      />

                      {/* Prestadores */}
                      <Route
                        path="prestadores"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Prestadores} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="prestadores/:id"
                        element={<PrestadorRedirect />}
                      />

                      <Route
                        path="prestador/perfil/:id"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.PrestadorPublicProfile} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="prestador/*"
                        element={<ExternalPrestadorRedirect />}
                      />

                      <Route
                        path="provider/dashboard"
                        element={<ExternalPrestadorRedirect subpath="dashboard" />}
                      />

                      {/* /admin/prestadores removido a 05/10/2026: página sem login que
                          listava telefone/email/morada de todos os prestadores. A
                          administração é só em admin.tamoaki.co.ao. */}

                      {/* Funcionalidades do utilizador */}
                      <Route
                        path="booking"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Booking} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="nearby-search"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.NearbySearch} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="favorites"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Favorites} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="appointments"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Appointments} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="atividade"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Atividade} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="avaliacoes"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Avaliacoes} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="avaliar/:id"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.AvaliarServico} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="messages"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Messages} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="promocoes"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Promocoes} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="promocoes/:id"
                        element={
                          <ProtectedRoute>
                            <PromocaoDetalhe />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="meus-tickets"
                        element={
                          <ProtectedRoute>
                            <MeusTickets />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="reportar-problema"
                        element={
                          <ProtectedRoute>
                            <ReportarProblema />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="auditoria"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <div className="p-8 text-center">
                                <h1 className="text-2xl font-bold mb-4">Auditoria</h1>
                                <p className="text-gray-600">Funcionalidade em desenvolvimento</p>
                              </div>
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="settings"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Settings} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="notificacoes"
                        element={
                          <ProtectedRoute>
                            <Suspense fallback={<PageLoading />}>
                              <LazyComponentWrapper config={LAZY_PAGES.Notificacoes} />
                            </Suspense>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="ajuda"
                        element={
                          <Suspense fallback={<PageLoading />}>
                            <LazyComponentWrapper config={LAZY_PAGES.Ajuda} />
                          </Suspense>
                        }
                      />

                      <Route
                        path="resultados"
                        element={<Navigate to="/prestadores" replace />}
                      />
                    </Route>

                    {/* Catch-all: página 404 */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>

                  <InstallPrompt />
                </div>
              </SentryErrorBoundary>
            </NotificationProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </ThemeProvider>

      {import.meta.env.DEV && (
        <div className="devtools-placeholder">
          {/* ReactQueryDevtools carregado dinamicamente em dev */}
        </div>
      )}
    </QueryClientProvider>
  )
}

export default App